/**
 * Linear focus cursor over an ordered list of card group ids.
 *
 * - Empty list → no focus (`null`).
 * - Nothing focused yet, or the focused id is gone → focus the first card.
 * - Otherwise step by `direction` and clamp at the ends (no wrap).
 */
export const moveFocus = (
  currentId: string | null,
  orderedIds: string[],
  direction: 1 | -1
): string | null => {
  if (orderedIds.length === 0) return null;
  const currentIndex = currentId === null ? -1 : orderedIds.indexOf(currentId);
  if (currentIndex === -1) return orderedIds[0] || null;
  const nextIndex = Math.min(orderedIds.length - 1, Math.max(0, currentIndex + direction));
  return orderedIds[nextIndex] || null;
};

/**
 * Keeps the cursor sensible after the card list changes.
 *
 * - Still present → keep it.
 * - Removed (e.g. its task was completed) → fall to whatever card now sits at
 *   its old index, so finishing a card advances to the next one.
 * - Board emptied, or the previous position is unknown → no focus (`null`).
 */
export const clampFocus = (
  focusedId: string | null,
  prevOrderedIds: string[],
  nextOrderedIds: string[]
): string | null => {
  if (focusedId === null) return null;
  if (nextOrderedIds.includes(focusedId)) return focusedId;
  if (nextOrderedIds.length === 0) return null;
  const prevIndex = prevOrderedIds.indexOf(focusedId);
  if (prevIndex === -1) return null;
  const nextIndex = Math.min(prevIndex, nextOrderedIds.length - 1);
  return nextOrderedIds[nextIndex] || null;
};
